import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, FileText, FileDown } from "lucide-react";

interface Funcionario { id: number; nome: string; }
interface Comprovante {
  idPontoRegistro: number;
  nsrFormatado: string;
  tipoBatidaTexto: string;
  sNomeTrabalhador: string;
  sCpfTrabalhador: string;
  sRazaoSocialEmpregador: string;
  sCnpjCpfEmpregador: string;
  sTipoRep: string;
  sNumeroRegistroInpi: string;
  sSerieRepP: string;
  sHashMarcacao: string;
  dtHoraMarcacao: string;
  dtEmissao: string;
}

const dt = (s: string) => (s ? new Date(s).toLocaleString("pt-BR") : "—");

export default function ComprovantesPage() {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [funcionarioId, setFuncionarioId] = useState("");
  const [itens, setItens] = useState<Comprovante[]>([]);
  const [sel, setSel] = useState<Comprovante | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    apiClient.get<Funcionario[]>("/api/rh/ponto/funcionarios", { params: { somenteAtivos: true } })
      .then((r) => setFuncionarios(r.data))
      .catch((e) => toast.error(e.message ?? "Falha ao carregar funcionários."));
  }, []);

  useEffect(() => {
    if (!funcionarioId) { setItens([]); setSel(null); return; }
    setCarregando(true);
    apiClient.get<Comprovante[]>("/api/rh/ponto/comprovantes/ultimas48h", { params: { funcionarioId: Number(funcionarioId) } })
      .then((r) => { setItens(r.data); setSel(r.data[0] ?? null); })
      .catch((e) => toast.error(e.message ?? "Falha ao carregar comprovantes."))
      .finally(() => setCarregando(false));
  }, [funcionarioId]);

  const baixarPdf = async (c: Comprovante) => {
    try {
      const resp = await apiClient.get(`/api/rh/ponto/comprovante/${c.idPontoRegistro}/pdf`, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data as Blob);
      const a = document.createElement("a");
      a.href = href; a.download = `Comprovante_${c.nsrFormatado}_REP_P.pdf`; a.click();
      URL.revokeObjectURL(href);
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao gerar o PDF.");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold">Comprovantes de Ponto</h1>
          <p className="text-sm text-muted-foreground">Últimas 48 horas (Portaria MTP 671/2021).</p>
        </div>
        {sel && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Imprimir
            </Button>
            <Button onClick={() => baixarPdf(sel)}>
              <FileDown className="h-4 w-4 mr-2" />Baixar PDF
            </Button>
          </div>
        )}
      </div>

      <div className="max-w-sm space-y-1 print:hidden">
        <Label>Funcionário</Label>
        <Select value={funcionarioId} onValueChange={setFuncionarioId}>
          <SelectTrigger><SelectValue placeholder="Selecione…" /></SelectTrigger>
          <SelectContent>
            {funcionarios.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.nome}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-2 print:hidden">
          {carregando ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : itens.length === 0 ? (
            <p className="text-sm text-muted-foreground">{funcionarioId ? "Sem marcações nas últimas 48h." : "Selecione um funcionário."}</p>
          ) : itens.map((c) => (
            <button
              key={c.idPontoRegistro}
              onClick={() => setSel(c)}
              className={`w-full text-left p-3 border rounded-lg hover:bg-muted/40 ${sel?.idPontoRegistro === c.idPontoRegistro ? "border-primary bg-muted/30" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{dt(c.dtHoraMarcacao)}</span>
                <Badge variant="secondary">{c.tipoBatidaTexto}</Badge>
              </div>
              <div className="text-xs text-muted-foreground">NSR {c.nsrFormatado}</div>
            </button>
          ))}
        </div>

        {sel && (
          <Card className="lg:col-span-2 font-mono text-sm">
            <CardHeader className="text-center border-b">
              <CardTitle className="text-base flex items-center justify-center gap-2">
                <FileText className="h-4 w-4" />COMPROVANTE DE REGISTRO DE PONTO DO TRABALHADOR
              </CardTitle>
              <p className="text-xs text-muted-foreground">Portaria MTP nº 671/2021 — {sel.sTipoRep}</p>
            </CardHeader>
            <CardContent className="pt-4 space-y-1">
              <Campo r="Empregador" v={sel.sRazaoSocialEmpregador} />
              <Campo r="CNPJ/CPF" v={sel.sCnpjCpfEmpregador} />
              <hr className="my-2" />
              <Campo r="Trabalhador" v={sel.sNomeTrabalhador} />
              <Campo r="CPF" v={sel.sCpfTrabalhador} />
              <hr className="my-2" />
              <Campo r="Marcação" v={`${dt(sel.dtHoraMarcacao)}  (${sel.tipoBatidaTexto})`} />
              <Campo r="NSR" v={sel.nsrFormatado} />
              <Campo r="REP" v={`${sel.sTipoRep}   INPI: ${sel.sNumeroRegistroInpi || "—"}   Série: ${sel.sSerieRepP || "—"}`} />
              <Campo r="Hash SHA-256" v={sel.sHashMarcacao} quebra />
              <hr className="my-2" />
              <Campo r="Emitido em" v={dt(sel.dtEmissao)} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function Campo({ r, v, quebra }: { r: string; v: string; quebra?: boolean }) {
  return (
    <div className="flex gap-2">
      <span className="w-32 shrink-0 text-muted-foreground">{r}:</span>
      <span className={quebra ? "break-all" : ""}>{v || "—"}</span>
    </div>
  );
}
